/**
 * Everything a frame needs, resolved once: loaded images, pixel rects, pose
 * segments and caption lines.
 *
 * The same scene object feeds the form preview, the playback preview and the
 * MP4 encoder, so what you see while setting up is exactly what gets exported.
 */
import { getCharacter } from "./characters";
import { getLayout, type ResolvedLayout } from "./layout";
import { normalizeHex, recolouredSvgUrl } from "./theme";
import type {
	CaptionLine,
	ComparisonSpec,
	NarrationResult,
	PoseKind,
	PoseSegment,
	WatermarkCorner,
} from "./types";

export interface SceneCard {
	label: string;
	image: HTMLImageElement | null;
}

export interface Scene {
	layout: ResolvedLayout;
	backgroundColor: string;
	backgroundImage: HTMLImageElement | null;
	thisCard: SceneCard;
	thatCard: SceneCard;
	poseImages: Record<PoseKind, HTMLImageElement>;
	/** Accent for the name chips and the caption highlight; already in the art. */
	themeColor: string;
	poses: PoseSegment[];
	captions: CaptionLine[];
	showCaptions: boolean;
	watermarkText: string;
	watermarkImage: HTMLImageElement | null;
	watermarkCorner: WatermarkCorner;
	durationMs: number;
}

/** Shown in the form preview so the caption band isn't an empty promise. */
const PLACEHOLDER_CAPTION = "Your narration appears here";

export async function buildScene({
	spec,
	narration,
}: {
	spec: ComparisonSpec;
	narration?: NarrationResult | null;
}): Promise<Scene> {
	const character = getCharacter({ id: spec.characterId });
	const themeColor = normalizeHex({ color: spec.themeColor });
	const pose = ({ url }: { url: string }) =>
		recolouredSvgUrl({ url, color: themeColor }).then((src) =>
			loadImage({ url: src }),
		);

	const [
		backgroundImage,
		thisImage,
		thatImage,
		watermarkImage,
		posePointThis,
		posePointThat,
		poseIdle,
		poseThinking,
	] = await Promise.all([
		loadOptional({ url: spec.background?.previewUrl }),
		loadOptional({ url: spec.thisImage?.previewUrl }),
		loadOptional({ url: spec.thatImage?.previewUrl }),
		loadOptional({ url: spec.watermarkImage?.previewUrl }),
		pose({ url: character.poses.this }),
		pose({ url: character.poses.that }),
		pose({ url: character.poses.idle }),
		pose({ url: character.poses.thinking }),
	]);

	const durationMs = narration?.durationMs ?? 0;

	return {
		layout: getLayout({ aspect: spec.aspect }),
		backgroundColor: spec.backgroundColor,
		backgroundImage,
		thisCard: { label: spec.thisLabel, image: thisImage },
		thatCard: { label: spec.thatLabel, image: thatImage },
		poseImages: {
			this: posePointThis,
			that: posePointThat,
			idle: poseIdle,
			thinking: poseThinking,
		},
		themeColor,
		poses: narration?.poses ?? [],
		captions: narration?.captions ?? placeholderCaptions(),
		showCaptions: spec.showCaptions,
		watermarkText: spec.watermarkText.trim(),
		watermarkImage,
		watermarkCorner: spec.watermarkCorner,
		durationMs,
	};
}

function placeholderCaptions(): CaptionLine[] {
	const words = PLACEHOLDER_CAPTION.split(" ").map((text, index) => ({
		text,
		startMs: index * 400,
		endMs: (index + 1) * 400,
	}));
	return [
		{
			words,
			startMs: 0,
			endMs: Number.MAX_SAFE_INTEGER,
		},
	];
}

export function poseAt({
	scene,
	timeMs,
}: {
	scene: Scene;
	timeMs: number;
}): PoseKind {
	for (const segment of scene.poses) {
		if (timeMs >= segment.startMs && timeMs < segment.endMs) return segment.pose;
	}
	return scene.poses.at(-1)?.pose ?? "idle";
}

async function loadOptional({
	url,
}: {
	url?: string;
}): Promise<HTMLImageElement | null> {
	if (!url) return null;
	try {
		return await loadImage({ url });
	} catch {
		// A dead image shouldn't sink the whole render; the slot just stays empty.
		return null;
	}
}

export function loadImage({ url }: { url: string }): Promise<HTMLImageElement> {
	return new Promise((resolve, reject) => {
		const image = new window.Image();
		image.crossOrigin = "anonymous";
		image.onload = () => resolve(image);
		image.onerror = () => reject(new Error(`Could not load ${url}`));
		image.src = url;
	});
}
